import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';

import { Label } from '../ui/label';

import { CheckCircle, Loader2, X, Upload } from 'lucide-react';

// Sélecteur unifié d'activités (comme dans RegisterPage)

import { formatCodeNAF } from '../../utils/codeNaf';

// Auto-complétion SIRET (composant canonique app-wide)

// 🔴 CONFIGURATION CENTRALISÉE - SOURCE UNIQUE
import { getIndicatifByCode, validatePhone, INDICATIFS_TELEPHONIQUES, STATUTS_JURIDIQUES } from '../../config/membreFormConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Normalisation import presse-papier : casse/accents ignorés
const _norm = (s) => (s || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();

// « mayotte » → « MAYOTTE » (nom canonique de la liste des territoires)
const resolveTerritoire = (value, territoires) => {
  if (!value) return '';
  const v = _norm(value);
  const hit = (territoires || []).find((t) => _norm(t.nom) === v || _norm(t.code) === v);
  return hit ? hit.nom : value;
};

// « sas » → « SAS » si la valeur correspond à un statut connu
const resolveStatutJuridique = (value) => {
  if (!value) return '';
  const v = value.toString().trim().toUpperCase();
  const hit = STATUTS_JURIDIQUES.find((s) => s.value === v || s.label.toUpperCase() === v);
  return hit ? hit.value : '';
};

// Éclate « +590 690… » en indicatif + numéro national
const splitPhoneIndicatif = (phone) => {
  const raw = (phone || '').toString().trim();
  if (!raw.startsWith('+')) return { indicatif: null, national: raw };
  const digits = raw.replace(/[^\d+]/g, '');
  const codes = [...new Set(INDICATIFS_TELEPHONIQUES.map((i) => i.code))].sort((a, b) => b.length - a.length);
  const code = codes.find((c) => digits.startsWith(c));
  if (!code) return { indicatif: null, national: raw.replace(/[^\d\s]/g, '') };
  return { indicatif: code, national: digits.slice(code.length) };
};

export function useMembreForm({ open, onOpenChange, onSuccess, getAuthHeader, creatorRole = 'cooper', initialValues = null }) {
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    indicatif_pays: '+33',
    role: 'beneficiaire_pro',
    raison_sociale: '',
    siret: '',
    statut_juridique: '',
    code_naf: '',
    territoire: '',
    pays: 'FR',
    adresse: '',
    code_postal: '',
    ville: '',
    activite_professionnelle: null
  });
  
  // État pour les documents à télécharger
  const [documents, setDocuments] = useState({
    // Documents professionnels
    kbis_inpi: null,
    piece_identite_gerant: null,
    attestation_ftpe: null,
    // Documents particuliers
    piece_identite: null
  });
  const [uploadingDoc, setUploadingDoc] = useState(null);
  
  const [territoires, setTerritoires] = useState([]);
  const [creating, setCreating] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Callback d'auto-complétion SIRET → remplit raison_sociale + code_naf
  const handleSiretAutoComplete = (company) => {
    if (!company) return;
    setFormData((prev) => ({
      ...prev,
      raison_sociale: prev.raison_sociale || company.raison_sociale || '',
      code_naf: prev.code_naf || formatCodeNAF(company.code_naf) || '',
    }));
  };

  useEffect(() => {
    if (open) fetchTerritoires();
  }, [open]);

  // V37.20 — Pré-remplissage depuis presse-papier (initialValues)
  useEffect(() => {
    if (open && initialValues && typeof initialValues === 'object') {
      const cleaned = Object.fromEntries(
        Object.entries(initialValues).filter(([_, v]) => v != null && v !== '')
      );
      // Statut juridique : n'injecter que si la valeur est reconnue
      if (cleaned.statut_juridique) {
        const resolved = resolveStatutJuridique(cleaned.statut_juridique);
        if (resolved) cleaned.statut_juridique = resolved;
        else delete cleaned.statut_juridique;
      }
      // Téléphone : extraire l'indicatif pays si présent (« +590 … »)
      if (cleaned.telephone) {
        const { indicatif, national } = splitPhoneIndicatif(cleaned.telephone);
        cleaned.telephone = national;
        if (indicatif) cleaned.indicatif_pays = indicatif;
      }
      setFormData((prev) => ({
        ...prev,
        ...cleaned,
      }));
    }
    // eslint-disable-next-line
  }, [open, initialValues && JSON.stringify(initialValues)]);

  // Canonicalise le territoire importé (« mayotte » → « MAYOTTE ») dès que la
  // liste des territoires est chargée (Select Radix n'affiche que les valeurs exactes)
  useEffect(() => {
    if (!formData.territoire || territoires.length === 0) return;
    if (territoires.some((t) => t.nom === formData.territoire)) return;
    const resolved = resolveTerritoire(formData.territoire, territoires);
    if (resolved !== formData.territoire) {
      setFormData((prev) => ({ ...prev, territoire: resolved }));
    }
    // eslint-disable-next-line
  }, [territoires, formData.territoire]);

  const fetchTerritoires = async () => {
    try {
      const response = await axios.get(`${API}/territoires`);
      setTerritoires(response.data || []);
    } catch {
    }
  };

  // Renvoyer le magic link de bienvenue
  const handleSendCredentialsByEmail = async () => {
    if (!createdCredentials) return;
    
    setSendingEmail(true);
    try {
      const { data } = await axios.post(
        `${API}/cooper/membres/resend-welcome-email`,
        { email: createdCredentials.email },
        { headers: getAuthHeader() }
      );
      // Mettre à jour le magic link affiché si un nouveau a été généré
      if (data?.magic_link) {
        setCreatedCredentials((prev) => prev ? { ...prev, magic_link: data.magic_link } : prev);
      }
      setEmailSent(true);
      toast.success('Lien magique renvoyé !', {
        description: `Un nouvel email a été envoyé à ${createdCredentials.email}`,
        duration: 5000
      });
    } catch (error) {
      toast.error('Erreur lors de l\'envoi de l\'email', {
        description: error.response?.data?.detail || 'Veuillez réessayer'
      });
    } finally {
      setSendingEmail(false);
    }
  };

  // Validation du téléphone avec config centralisée
  const selectedIndicatif = getIndicatifByCode(formData.indicatif_pays || '+33');
  const phoneValidation = validatePhone(formData.telephone || '', formData.indicatif_pays || '+33');

  // Fonction de téléchargement de document
  const handleDocumentUpload = async (e, docType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Vérifier le type de fichier
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format accepté : PDF, JPG, PNG', {
        description: 'Le fichier sélectionné n\'est pas dans un format valide.',
        duration: 5000
      });
      return;
    }

    // Vérifier la taille (max 5MB) avec message d'alerte détaillé
    const maxSize = 5 * 1024 * 1024; // 5 MB
    if (file.size > maxSize) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      toast.error(`⚠️ Fichier trop volumineux : ${fileSizeMB} MB`, {
        description: `La taille maximum autorisée est de 5 MB. Veuillez compresser votre fichier ou choisir un fichier plus petit.`,
        duration: 8000,
        style: {
          background: '#FEF2F2',
          border: '1px solid #FCA5A5',
          color: '#991B1B'
        }
      });
      // Reset l'input file
      e.target.value = '';
      return;
    }

    setUploadingDoc(docType);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('document_type', docType);

      const response = await axios.post(
        `${API}/upload/document`,
        formDataUpload,
        { 
          headers: { 
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setDocuments(prev => ({
        ...prev,
        [docType]: {
          url: response.data.url,
          filename: file.name
        }
      }));
      toast.success(`✅ Document "${file.name}" téléchargé avec succès`, {
        duration: 3000
      });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Erreur lors du téléchargement';
      toast.error(`❌ ${errorMessage}`, {
        description: 'Veuillez réessayer ou contacter le support si le problème persiste.',
        duration: 6000
      });
    } finally {
      setUploadingDoc(null);
      // Reset l'input file pour permettre de re-sélectionner le même fichier
      e.target.value = '';
    }
  };

  // Fonction de rendu pour un champ de document
  const renderDocumentUploadField = (docType, label, description, required = false) => (
    <div className="border border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-400 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <Label className="text-sm font-medium text-gray-700">
            {label} {required && <span className="text-red-500">*</span>}
          </Label>
          {description && (
            <p className="text-xs text-gray-500 mt-1">{description}</p>
          )}
        </div>
        {documents[docType] ? (
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <span className="text-xs text-green-600 max-w-[120px] truncate">
              {documents[docType].filename}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setDocuments(prev => ({ ...prev, [docType]: null }))}
              className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <label className="cursor-pointer">
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => handleDocumentUpload(e, docType)}
              className="hidden"
              disabled={uploadingDoc === docType}
            />
            <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors">
              {uploadingDoc === docType ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              <span className="text-sm">Télécharger</span>
            </div>
          </label>
        )}
      </div>
    </div>
  );

  const validateForm = () => {
    const errors = {};
    
    if (!formData.nom?.trim()) errors.nom = 'Le nom est obligatoire';
    if (!formData.prenom?.trim()) errors.prenom = 'Le prénom est obligatoire';
    if (!formData.email?.trim()) errors.email = 'L\'email est obligatoire';
    if (!formData.telephone?.trim()) errors.telephone = 'Le téléphone est obligatoire';
    
    // Validation email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      errors.email = 'Format email invalide';
    }
    
    // Validation téléphone
    if (formData.telephone && !phoneValidation.isValid) {
      errors.telephone = `${phoneValidation.expectedLength} chiffres requis`;
    }
    
    // Champs pro obligatoires si role pro
    if (formData.role === 'beneficiaire_pro') {
      if (!formData.raison_sociale?.trim()) errors.raison_sociale = 'Raison sociale obligatoire';
      if (!formData.statut_juridique) errors.statut_juridique = 'Statut juridique obligatoire';
      if (!formData.code_naf?.trim()) errors.code_naf = 'Code NAF obligatoire';
      if (!formData.territoire) errors.territoire = 'Territoire obligatoire';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Veuillez remplir tous les champs obligatoires correctement');
      return;
    }

    setCreating(true);
    try {
      // Formater le téléphone complet
      const telephoneComplet = `${formData.indicatif_pays} ${formData.telephone}`;
      
      // Préparer les données avec les documents et le plan d'abonnement
      const memberData = {
        ...formData,
        telephone: telephoneComplet,
        documents: {
          kbis_inpi: documents.kbis_inpi?.url || null,
          piece_identite_gerant: documents.piece_identite_gerant?.url || null,
          attestation_ftpe: documents.attestation_ftpe?.url || null,
          piece_identite: documents.piece_identite?.url || null
        },
        // Ajout du plan d'abonnement si sélectionné (pour création wallet)
        subscription_plan: formData.subscription_plan || null
      };
      
      const response = await axios.post(
        `${API}/cooper/membres/creer`,
        memberData,
        { headers: getAuthHeader() }
      );

      // Message de succès adapté au plan choisi
      if (formData.subscription_plan && response.data.wallet_created) {
        const walletCredits = response.data.wallet_credits;
        toast.success(`Membre créé avec succès !`, {
          description: walletCredits ? `Wallet créé avec ${walletCredits} crédits` : 'Wallet créé',
          duration: 5000
        });
      } else {
        toast.success('Membre créé avec succès !');
      }
      
      setCreatedCredentials(response.data.credentials);
      onOpenChange(false);
      setShowCredentialsModal(true);
      resetForm();
      
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du membre');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      prenom: '',
      email: '',
      telephone: '',
      indicatif_pays: '+33',
      role: 'beneficiaire_pro',
      raison_sociale: '',
      siret: '',
      statut_juridique: '',
      code_naf: '',
      territoire: '',
      pays: 'FR',
      adresse: '',
      code_postal: '',
      ville: '',
      activite_professionnelle: null,
      subscription_plan: ''
    });
    setDocuments({
      kbis_inpi: null,
      piece_identite_gerant: null,
      attestation_ftpe: null,
      piece_identite: null
    });
    setFormErrors({});
  };

  const handleCopyCredentials = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papier');
  };

  // Titre selon le rôle du créateur
  const getTitle = () => {
    switch (creatorRole) {
      case 'super_admin':
        return 'Créer un nouveau membre (Super Admin)';
      case 'admin':
        return 'Créer un nouveau membre (Admin)';
      default:
        return 'Créer un nouveau membre';
    }
  };

  return { createdCredentials, creating, creatorRole, documents, emailSent, fetchTerritoires, formData, formErrors, getAuthHeader, getTitle, handleCopyCredentials, handleDocumentUpload, handleSendCredentialsByEmail, handleSiretAutoComplete, handleSubmit, initialValues, onOpenChange, onSuccess, open, phoneValidation, renderDocumentUploadField, resetForm, selectedIndicatif, sendingEmail, showCredentialsModal, territoires, uploadingDoc, validateForm, setShowCredentialsModal, setDocuments, setFormErrors, setSendingEmail, setFormData, setTerritoires, setEmailSent, setUploadingDoc, setCreatedCredentials, setCreating };
}
